/**
 * Ground point -> viewport CSS pixels, for the HUD's in-world indicators. The exact inverse of
 * `PixelCamera.screenToGround`, then the same integer upscale the pipeline applies.
 *
 * It must not round: the HUD samples it at several points to recover the camera's affine map,
 * and uses that to place off-screen sounds and to size the noise ring.
 */

import { type PixelCamera, VIEW_H, VIEW_W } from '../render/camera/PixelCamera';
import type { RenderPipeline } from '../render/pipeline/RenderPipeline';
import type { Projector } from '../ui/hud';

export function groundProjector(camera: PixelCamera, pipeline: RenderPipeline): Projector {
  return (x, z) => {
    const ppm = camera.pixelsPerMetre;
    const tx = x * ppm - camera.camTexel.x - camera.subTexel.x + VIEW_W / 2;
    const ty = VIEW_H / 2 + z * camera.sinPitch * ppm + camera.camTexel.y + camera.subTexel.y;
    if (tx < 0 || tx > VIEW_W || ty < 0 || ty > VIEW_H) return null;
    const view = pipeline.viewRect;
    const scale = view.width / VIEW_W;
    return { x: view.left + tx * scale, y: view.top + ty * scale };
  };
}
